import { ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import {
  ShipmentDetailFormData,
  ShipmentOrderResponseEntity,
  ShipmentOrderWaypointTimeEntity,
} from 'src/entities/ShipmentOrderResponse.entity';
import {
  mergeConstraintSelectionsByMasterOrder,
} from 'src/utils/laneBasicInfoMerge';
import { getEarliestDateFormatted } from 'src/utils/shipmentLegRunItems';
import {
  processWaypointTimes,
  validateLoadingUnloadingDateRangeForWaypointTimes,
} from 'src/utils/shipmentOrderWaypointTimes';
import { VehicleAttributeOptionsOf } from 'src/utils/vehicleAttributeOptions';

/** レーン基本情報タブの編集ドラフト（全伝票に同一内容を適用） */
export type LaneBasicInfoDraft = {
  loadingWaypointTimes: ShipmentOrderWaypointTimeEntity[];
  unloadingWaypointTimes: ShipmentOrderWaypointTimeEntity[];
  loadingStayingMinutes: number | '';
  unloadingStayingMinutes: number | '';
  designatedTruckKlasses: ShipmentOrderResponseEntity['designatedTruckKlasses'];
  designatedTruckCarModels: ShipmentOrderResponseEntity['designatedTruckCarModels'];
  designatedTruckLoadingPlatformHeights: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformHeights'];
  designatedTruckLoadingPlatformWidths: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformWidths'];
  designatedTruckLoadingPlatformLengths: ShipmentOrderResponseEntity['designatedTruckLoadingPlatformLengths'];
  designatedTruckFloorSpecifications: ShipmentOrderResponseEntity['designatedTruckFloorSpecifications'];
  designatedTruckFeatures: ShipmentOrderResponseEntity['designatedTruckFeatures'];
  allowedTruckIds: number[];
  deniedDriverIds: number[];
};

export const cloneWaypointTimes = (
  times: ShipmentOrderWaypointTimeEntity[] | undefined,
): ShipmentOrderWaypointTimeEntity[] =>
  JSON.parse(JSON.stringify(times ?? [])) as ShipmentOrderWaypointTimeEntity[];

/**
 * @param optionsOf 会社ごとの車両条件マスタ（#329 P8）。並び順の正本はここで、リテラル配列ではない。
 *   マスタに無い現在値は `mergeConstraintSelectionsByMasterOrder` が末尾に残す（設計書 §5-4）ので、
 *   現在値は渡さなくてよい。
 *
 * Why not この util の中でマスタを取りに行く: 純粋関数のままにしておきたい。
 *   取得は呼び出し元の hook（`useLaneDetailEdit`）が持つ。
 */
export const createLaneBasicInfoDraftFromDetail = (
  detail: ShipmentOrderLaneDetail,
  optionsOf: VehicleAttributeOptionsOf,
): LaneBasicInfoDraft | null => {
  const orders = detail.shipmentOrders;
  if (!orders?.length) return null;

  const first = orders[0];

  return {
    loadingWaypointTimes: cloneWaypointTimes(
      first.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes,
    ),
    unloadingWaypointTimes: cloneWaypointTimes(
      first.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes,
    ),
    loadingStayingMinutes: first.shipmentOrderLoadingWaypoint.stayingMinutes ?? '',
    unloadingStayingMinutes: first.shipmentOrderUnloadingWaypoint.stayingMinutes ?? '',
    designatedTruckKlasses: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckKlasses,
      optionsOf('klass', []),
    ),
    designatedTruckCarModels: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckCarModels,
      optionsOf('car_model', []),
    ),
    designatedTruckLoadingPlatformHeights: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformHeights,
      optionsOf('loading_platform_height', []),
    ),
    designatedTruckLoadingPlatformWidths: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformWidths,
      optionsOf('loading_platform_width', []),
    ),
    designatedTruckLoadingPlatformLengths: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformLengths,
      optionsOf('loading_platform_length', []),
    ),
    designatedTruckFloorSpecifications: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFloorSpecifications,
      optionsOf('floor_specification', []),
    ),
    designatedTruckFeatures: mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFeatures,
      optionsOf('features', []),
    ),
    allowedTruckIds: [...new Set(orders.flatMap((o) => o.allowedTruckIds ?? []))],
    deniedDriverIds: [...new Set(orders.flatMap((o) => o.deniedDriverIds ?? []))],
  };
};

/**
 * 伝票 1 件へ書き戻す車両条件を選ぶ。#329
 *
 * ドラフトの車両条件は全伝票の**和集合**（画面にその値を出さないと、持っている伝票があることが
 * 分からず外すこともできないため・設計書 §5-4）。これをそのまま全伝票へ書き戻すと、
 * 無効化された値を元から持たない伝票にとっては「属性の変更」になり、
 * API の inclusion 検証（`ShipmentOrder#validate_designated_truck_requirements` の
 * `will_save_change_to_attribute?` ゲートを通過して `invalid_values` で弾かれる）で 422 になる。
 * **車両条件を一切触らずに「保存」を押しただけで、レーン本体だけ保存されて伝票が保存されない。**
 *
 * API の検証は「有効なマスタ ∪ そのレコードが元から持っていた値」なので、**他の伝票だけが持つ
 * 無効値は、この伝票にとっては新規追加**であり、渡せば弾かれる。API 側が緩んでもここは要る。
 *
 * そこで「有効なマスタに在る値」または「その伝票が元から持っていた値」だけを残す。
 * 画面で付けた値は必ずマスタ由来なので全伝票へ行き渡り、和集合で紛れ込んだ他の伝票の
 * 無効値は自分の伝票にだけ残る。利用者が外したときはドラフトから消えるので全伝票から消える。
 *
 * Why not 表示側の和集合でも落とす: 落とすと、その値を持つ伝票があることが画面から分からなくなる。
 * **表示の和集合と、書き戻す伝票ごとの値は別物**として扱う。
 */
const constraintValuesForOrder = (
  draftValues: string[] | undefined,
  orderValues: string[] | undefined,
  activeMasterNames: string[],
): string[] => {
  const active = new Set(activeMasterNames);
  const owned = new Set(orderValues ?? []);

  return (draftValues ?? []).filter((value) => active.has(value) || owned.has(value));
};

/**
 * 1 件の伝票にドラフトを適用した PATCH 用ペイロードを組み立てる。
 * 時間枠は伝票ごとの元 ID を保持したまま processWaypointTimes する。
 *
 * @param optionsOf 会社ごとの車両条件マスタ（#329）。書き戻す値を伝票ごとに絞るのに要る。
 *   渡し忘れると 422 に戻るので必須にしてある。
 */
export const buildShipmentDetailFormDataForLaneSave = (
  order: ShipmentOrderResponseEntity,
  draft: LaneBasicInfoDraft,
  optionsOf: VehicleAttributeOptionsOf,
): ShipmentDetailFormData => {
  const processedLoading = processWaypointTimes(
    order.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [],
    draft.loadingWaypointTimes,
  );
  const processedUnloading = processWaypointTimes(
    order.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [],
    draft.unloadingWaypointTimes,
  );

  const loadingMin = draft.loadingStayingMinutes === '' ? '' : Number(draft.loadingStayingMinutes);
  const unloadingMin = draft.unloadingStayingMinutes === '' ? '' : Number(draft.unloadingStayingMinutes);

  const validLoadingTimes = processedLoading.filter((t) => !t._destroy && t.startAt);
  const validUnloadingTimes = processedUnloading.filter((t) => !t._destroy && t.startAt);

  return {
    ...order,
    // issue #283: 備考・任意項目は伝票ごとに異なる固有情報のため、レーンドラフトの値で
    // 全伝票を上書きしない。...order 由来の各伝票自身の元値をそのまま保持する。
    designatedTruckKlasses: constraintValuesForOrder(
      draft.designatedTruckKlasses, order.designatedTruckKlasses, optionsOf('klass', []),
    ),
    designatedTruckCarModels: constraintValuesForOrder(
      draft.designatedTruckCarModels, order.designatedTruckCarModels, optionsOf('car_model', []),
    ),
    designatedTruckLoadingPlatformHeights: constraintValuesForOrder(
      draft.designatedTruckLoadingPlatformHeights,
      order.designatedTruckLoadingPlatformHeights,
      optionsOf('loading_platform_height', []),
    ),
    designatedTruckLoadingPlatformWidths: constraintValuesForOrder(
      draft.designatedTruckLoadingPlatformWidths,
      order.designatedTruckLoadingPlatformWidths,
      optionsOf('loading_platform_width', []),
    ),
    designatedTruckLoadingPlatformLengths: constraintValuesForOrder(
      draft.designatedTruckLoadingPlatformLengths,
      order.designatedTruckLoadingPlatformLengths,
      optionsOf('loading_platform_length', []),
    ),
    designatedTruckFloorSpecifications: constraintValuesForOrder(
      draft.designatedTruckFloorSpecifications,
      order.designatedTruckFloorSpecifications,
      optionsOf('floor_specification', []),
    ),
    designatedTruckFeatures: constraintValuesForOrder(
      draft.designatedTruckFeatures, order.designatedTruckFeatures, optionsOf('features', []),
    ),
    allowedTruckIds: draft.allowedTruckIds,
    deniedDriverIds: draft.deniedDriverIds,
    shipmentOrderLoadingWaypoint: {
      ...order.shipmentOrderLoadingWaypoint,
      stayingMinutes: draft.loadingStayingMinutes,
      stayingSeconds: loadingMin === '' ? '' : loadingMin * 60,
      shipmentOrderWaypointLoadingTimes: processedLoading,
    },
    shipmentOrderUnloadingWaypoint: {
      ...order.shipmentOrderUnloadingWaypoint,
      stayingMinutes: draft.unloadingStayingMinutes,
      stayingSeconds: unloadingMin === '' ? '' : unloadingMin * 60,
      shipmentOrderWaypointUnloadingTimes: processedUnloading,
    },
    loadingOn: getEarliestDateFormatted(validLoadingTimes),
    unloadingOn: getEarliestDateFormatted(validUnloadingTimes),
    loadingTimeSlot: 'カスタム',
    unloadingTimeSlot: 'カスタム',
    sectionTypes: order.sectionTypes ?? [],
  };
};

export const validateLaneBasicInfoDraft = (
  orders: ShipmentOrderResponseEntity[],
  draft: LaneBasicInfoDraft,
): boolean => {
  for (const order of orders) {
    const pl = processWaypointTimes(
      order.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [],
      draft.loadingWaypointTimes,
    );
    const pu = processWaypointTimes(
      order.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [],
      draft.unloadingWaypointTimes,
    );
    if (!validateLoadingUnloadingDateRangeForWaypointTimes(pl, pu)) {
      return false;
    }
  }
  return true;
};
